/* eslint-disable max-lines */
import type { CSSProperties } from 'react';
import { calcSpacing } from 'src/components/foundations';
import { textStyleCss } from 'src/theme/atlas';
import styled, { css, keyframes } from 'styled-components';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const baseTextStyles = css`
  margin: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
`;

const flexColumnStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const flexCenterStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const customScrollbarStyles = css`
  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 2px;
    transition: background 0.2s ease;
  }

  &:hover::-webkit-scrollbar-thumb {
    background: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N400
        : props.theme.atlas.semantic.color.ui.scrollbar.handle};
  }
`;

const flexContainerWithGap = (gap: number) => css`
  ${flexColumnStyles}
  gap: ${calcSpacing(gap)}px;
`;

const scrollableContainerStyles = css`
  ${flexContainerWithGap(1)}
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  padding-right: ${calcSpacing(0.5)}px;
  ${customScrollbarStyles}
`;

const simpleTextStyles = css`
  margin: 0;
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
`;

const flexRowWithGap = (gap: number) => css`
  display: flex;
  gap: ${calcSpacing(gap)}px;
  width: 100%;
`;

const inputBoxStyles = css`
  background: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N0
      : props.theme.atlas.semantic.color.surface.default};
  border: 1px solid
    ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N400
        : props.theme.atlas.semantic.color.outline.default};
  border-radius: 3px;
  padding: 8px 16px;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBody
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  width: 100%;
  box-sizing: border-box;
`;

const iconButtonStyles = css`
  display: flex;
  align-items: center;
  gap: ${calcSpacing(1)}px;
  padding: 8px 16px;
  background: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N0
      : props.theme.atlas.semantic.color.surface.default};
  border: none;
  border-radius: 3px;
  cursor: pointer;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBody
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
`;

export const InlineSection = styled.section<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    gap: 12px;
  `}
`;

export const InlineContentWrapper = styled.div<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const InlineHeader = styled.div<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  `}
`;

export const InlineTitle = styled.h3<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  margin: 0;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleHeading
      : textStyleCss(theme.atlas.typography.semantic.body)}
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    font-family: Rubik, sans-serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 20px;
    letter-spacing: 0;
    margin-top: 4px;
    margin-bottom: 6px;
  `}
`;

export const InlineTitleBlock = styled.div`
  width: 100%;
`;

export const HeaderProgressBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${calcSpacing(1)}px;
  flex-shrink: 0;
`;

export const LanguageBlock = styled.div<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  display: flex;
  align-items: center;
  gap: 8px;

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    width: 100%;
    margin-bottom: ${calcSpacing(1)}px;
  `}
`;

export const LanguageRow = styled.div<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    justify-content: space-between;
    width: 100%;
  `}
`;

export const ContentTypePill = styled.span`
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBody
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N600
      : props.theme.atlas.semantic.color.type.muted};
  background-color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N200
      : props.theme.atlas.semantic.color.surface.variant};
  padding: 4px 10px;
  border-radius: 6px;
`;

export const PreviewContent = styled.div`
  ${flexContainerWithGap(3)}
  flex: 1;
  min-height: 0;
`;

export const LanguageSection = styled.div`
  ${flexContainerWithGap(1)}
`;

export const LanguageDropdownWrapper = styled.div`
  width: 100%;
  padding: 0px 20px 0px 0px;
  box-sizing: border-box;
`;

export const LanguageLabel = styled.p<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  ${simpleTextStyles}
  ${({ theme, $inlinePlacement }) =>
    $inlinePlacement === 'formManagement'
      ? theme.isVaultVariant
        ? theme.newTypography.textStyleBodySmall
        : textStyleCss(theme.atlas.typography.semantic.caption)
      : theme.isVaultVariant
        ? theme.newTypography.textStyleHeading
        : textStyleCss(theme.atlas.typography.semantic.body)};
`;

export const PreviewMainSection = styled.div<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  ${flexContainerWithGap(2)}
  align-items: flex-end;
  flex: 1;
  min-height: 0;
  position: relative;

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    width: 100%;
  `}
`;

export const TranslatingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 10000;
  pointer-events: auto;
  border-radius: 12px;
`;

export const TranslatingOverlayStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${calcSpacing(3)}px;
  max-width: 420px;
  padding: 0 ${calcSpacing(2)}px;
`;

export const TranslatingOverlayCopy = styled.p`
  margin: 0;
  font-family: Rubik, sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
  text-align: center;
`;

export const FormPreviewCard = styled.div<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  background-color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N0
      : props.theme.atlas.semantic.color.surface.default};
  border: 1px solid #dadada;
  border-radius: 12px;
  padding: ${calcSpacing(3)}px ${calcSpacing(3)}px 0 ${calcSpacing(3)}px;
  ${flexColumnStyles}
  box-sizing: border-box;
  justify-content: space-between;
  flex: 1;
  min-height: 0;

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    width: 100%;
    min-height: 835px;
    height: 835px;
    padding-left: ${calcSpacing(3)}px;
    padding-right: ${calcSpacing(3)}px;
    flex: none;
    flex-shrink: 0;
  `}
`;

export const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: ${calcSpacing(2)}px 0 ${calcSpacing(3)}px 0;
  border-top: 1px solid #dadada;
`;

export const ProgressBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${calcSpacing(2)}px;
  width: 100%;
`;

export const ProgressText = styled.div`
  ${simpleTextStyles}
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmallSemibold
      : textStyleCss(theme.atlas.typography.semantic.caption)};
  font-weight: 600;
  white-space: nowrap;
  margin-top: 13px;
`;

export const ProgressBarWrapper = styled.div`
  flex: 1;
  min-width: 0;

  & > div {
    width: 100%;
  }

  p {
    display: none;
  }
`;

export const FormContentSection = styled.div`
  ${flexContainerWithGap(3)}
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
  min-height: 0;
`;

export const QuestionContentSection = styled.div<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  ${flexContainerWithGap(2)}

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    justify-content: center;
    align-items: center;
    flex: 1;
  `}
`;

export const QuestionContentInner = styled.div<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  ${flexContainerWithGap(2)}
  width: 100%;
  min-width: 0;

  textarea,
  input[type='text'],
  input[type='number'] {
    box-sizing: border-box;
    max-width: 100%;
  }

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    width: 80%;
  `}
`;

export const QuestionTextSection = styled.div<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  ${flexContainerWithGap(1)}

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    align-items: center;
    width: 100%;
  `}
`;

export const FormTitle = styled.h2`
  ${baseTextStyles}
  ${(props) => props.theme.newTypography.textStyleDisplayMedium};
  font-weight: 400;
  text-align: center;
`;

export const QuestionTitle = styled.h2<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  ${baseTextStyles}
  ${(props) => props.theme.newTypography.textStyleBodySemibold};
  font-weight: 600;
  text-align: left;

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    font-family: Rubik, sans-serif;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0;
    text-align: center;
  `}
`;

export const FormDescription = styled.div<{
  $isRichText?: boolean;
}>`
  ${baseTextStyles}
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBody
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  width: 100%;
  text-align: ${(props) => (props.$isRichText ? 'left' : 'center')};

  .ql-container {
    font-family: inherit;
    font-size: inherit;
  }

  .ql-editor {
    padding: 0;
    border: none;
    overflow: visible;
  }

  .ql-editor.ql-blank::before {
    font-style: normal;
    color: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N600
        : props.theme.atlas.semantic.color.type.muted};
  }
`;

export const QuestionDescription = styled.div<{
  $inlinePlacement?: 'standard' | 'formManagement';
  $isRichText?: boolean;
}>`
  ${baseTextStyles}
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmall
      : textStyleCss(theme.atlas.typography.semantic.caption)};
  width: 100%;
  text-align: left;
  max-height: 150px;
  overflow-y: auto;
  overflow-x: hidden;
  ${customScrollbarStyles}

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    font-family: Rubik, sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
    letter-spacing: 0;
    text-align: ${props.$isRichText ? 'left' : 'center'};
  `}

  .ql-container {
    font-family: inherit;
    font-size: inherit;
  }

  .ql-editor {
    padding: 0;
    border: none;
    overflow: visible;
  }

  .ql-editor.ql-blank::before {
    font-style: normal;
    color: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N600
        : props.theme.atlas.semantic.color.type.muted};
  }
`;

export const FormDescriptionScrollArea = styled.div`
  ${flexColumnStyles}
  width: 100%;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 8px;
  ${customScrollbarStyles}

  scrollbar-gutter: stable;
`;

export const ButtonWrapper = styled.div`
  display: flex;
  gap: ${calcSpacing(1.5)}px;
  align-items: center;
  justify-content: flex-end;

  button[data-testid$='-next-button'] {
    border-radius: 8px;
    min-width: auto;
    padding: 8px 16px;
  }
`;

export const NavButton = styled.button<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  ${iconButtonStyles}
  justify-content: center;

  &:hover:not(:disabled) {
    font-weight: 600;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    & *,
    & i {
      cursor: pointer;
    }
  `}
`;

export const previewBodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  margin: 0,
  padding: '0 20px 20px 20px',
  width: 'auto',
};

export const RadioOptionsGroup = styled.div<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  ${scrollableContainerStyles}

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    max-height: none;
    overflow-y: visible;
  `}
`;

export const RadioOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${calcSpacing(1)}px;

  input,
  .uil-check,
  .uil-minus {
    cursor: pointer;
  }
`;

export const OtherOptionInputWrapper = styled.div`
  margin-top: ${calcSpacing(1)}px;
  width: 100%;
`;

export const AttachmentContainer = styled.div`
  ${flexContainerWithGap(1)}
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
`;

export const FileCardsList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  ${customScrollbarStyles}
`;

export const AttachmentUploadArea = styled.div<{ $hasFiles?: boolean }>`
  border: none;
  border-radius: 3px;
  padding: ${calcSpacing(3)}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${calcSpacing(1)}px;
  background-color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N0
      : props.theme.atlas.semantic.color.surface.default};
  background-image:
    repeating-linear-gradient(
      0deg,
      #bdbdbd,
      #bdbdbd 10px,
      transparent 10px,
      transparent 20px,
      #bdbdbd 20px
    ),
    repeating-linear-gradient(
      90deg,
      #bdbdbd,
      #bdbdbd 10px,
      transparent 10px,
      transparent 20px,
      #bdbdbd 20px
    ),
    repeating-linear-gradient(
      180deg,
      #bdbdbd,
      #bdbdbd 10px,
      transparent 10px,
      transparent 20px,
      #bdbdbd 20px
    ),
    repeating-linear-gradient(
      270deg,
      #bdbdbd,
      #bdbdbd 10px,
      transparent 10px,
      transparent 20px,
      #bdbdbd 20px
    );
  background-size:
    2px 100%,
    100% 2px,
    2px 100%,
    100% 2px;
  background-position:
    0 0,
    0 0,
    100% 0,
    0 100%;
  background-repeat: no-repeat;
  width: 100%;
  flex: ${(props) => (props.$hasFiles ? 'none' : '1')};
  min-height: ${(props) => (props.$hasFiles ? 'auto' : '150px')};
  box-sizing: border-box;
`;

export const AttachmentIconWrapper = styled.div`
  ${flexCenterStyles}
`;

export const AttachmentText = styled.p`
  ${baseTextStyles}
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBody
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  text-align: center;
  line-height: 1.5;
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
`;

export const AttachmentBrowseLink = styled.span`
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
`;

export const AttachmentFileLimit = styled.p`
  ${simpleTextStyles}
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmall
      : textStyleCss(theme.atlas.typography.semantic.caption)};
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N600
      : props.theme.atlas.semantic.color.type.muted};
  text-align: center;
  margin-top: ${calcSpacing(1)}px;
`;

export const FileCard = styled.div`
  background-color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N0
      : props.theme.atlas.semantic.color.surface.default};
  border: 1px solid #dadada;
  border-radius: 0;
  padding: ${calcSpacing(2)}px;
  display: flex;
  align-items: center;
  gap: ${calcSpacing(1.5)}px;
  width: 100%;
  margin-top: -1px;
  box-sizing: border-box;
  min-width: 0;

  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    margin-top: 0;
  }

  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

export const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

export const FileName = styled.p`
  ${simpleTextStyles}
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBody
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FileSize = styled.p`
  ${simpleTextStyles}
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmall
      : textStyleCss(theme.atlas.typography.semantic.caption)};
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N600
      : props.theme.atlas.semantic.color.type.muted};
`;

export const FileError = styled.p`
  ${simpleTextStyles}
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmall
      : textStyleCss(theme.atlas.typography.semantic.caption)};
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N600
      : props.theme.atlas.semantic.color.type.muted};
`;

export const SpinningIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${spin} 1s linear infinite;
`;

export const CircularProgress = styled.svg<{ $progress: number }>`
  width: 24px;
  height: 24px;
  transform: rotate(-90deg);

  circle {
    &.inner {
      fill: ${(props) =>
        props.theme.isVaultVariant
          ? props.theme.newPalette.neutral.N100
          : props.theme.atlas.semantic.color.surface.subtle};
      stroke: none;
    }

    &.bg {
      fill: none;
      stroke: ${(props) =>
        props.theme.isVaultVariant
          ? props.theme.newPalette.neutral.N300
          : props.theme.atlas.semantic.color.outline.default};
      stroke-width: 4;
    }

    &.progress {
      fill: none;
      stroke: ${(props) =>
        props.theme.isVaultVariant
          ? props.theme.newPalette.neutral.N800
          : props.theme.atlas.semantic.color.type.default};
      stroke-width: 4;
      stroke-dasharray: ${2 * Math.PI * 10};
      stroke-dashoffset: ${(props) =>
        2 * Math.PI * 10 * (1 - props.$progress / 100)};
      transition: stroke-dashoffset 0.3s ease;
      stroke-linecap: round;
    }
  }
`;

export const DeleteIconWrapper = styled.button`
  ${flexCenterStyles}
  cursor: pointer;
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  width: 24px;
  height: 24px;

  & > * {
    pointer-events: none;
  }

  &:hover {
    opacity: 0.7;
  }
`;

export const FileProgressBarWrapper = styled.div`
  width: 100%;
  height: 4px;
  background-color: ${(props) => props.theme.newPalette.neutral.N300};
  border-radius: 2px;
  margin-top: 4px;
`;

export const FileProgressBar = styled.div<{ $progress: number }>`
  height: 100%;
  background-color: ${(props) => props.theme.newPalette.turquoise.T300};
  border-radius: 2px;
  width: ${(props) => props.$progress}%;
  transition: width 0.3s ease;
`;

export const FileProgressText = styled.p`
  ${simpleTextStyles}
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmall
      : textStyleCss(theme.atlas.typography.semantic.caption)};
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N600
      : props.theme.atlas.semantic.color.type.muted};
`;

export const AttachmentInfoFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  width: 100%;
`;

export const AttachmentInfoFooterIconWrap = styled.span`
  display: flex;
  flex-shrink: 0;
  line-height: 0;
`;

export const AttachmentInfoFooterText = styled.p`
  ${simpleTextStyles}
  max-width: 100%;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
  flex: 1;
  min-width: 0;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmall
      : textStyleCss(theme.atlas.typography.semantic.caption)};
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N700
      : props.theme.atlas.semantic.color.type.muted};
`;

export const AttachmentHelpArticlePhrase = styled.span`
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmallSemibold
      : textStyleCss(theme.atlas.typography.semantic.caption)};
  font-weight: 600;
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
  text-decoration: underline;
  cursor: default;
`;

export const DropdownWrapper = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;
  width: 100%;
  padding-right: ${calcSpacing(3)}px;
  box-sizing: border-box;
`;

export const DataGridContainer = styled.div<{
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  ${flexContainerWithGap(1)}
  overflow: auto;
  padding: 4px 0 4px 4px;
  ${customScrollbarStyles}

  &::-webkit-scrollbar {
    height: 3px;
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
  }

  &:hover::-webkit-scrollbar-thumb {
    background: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N400
        : props.theme.atlas.semantic.color.ui.scrollbar.handle};
  }

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    overflow: visible;
  `}
`;

export const DataGridRow = styled.div`
  display: flex;
  gap: ${calcSpacing(1)}px;
  flex-shrink: 0;

  &::after {
    content: '';
    min-width: 4px;
    flex-shrink: 0;
  }
`;

export const DataGridColumn = styled.div<{
  $columnCount?: number;
  $inlinePlacement?: 'standard' | 'formManagement';
}>`
  flex: 0 0
    calc(
      (
          100% - ${calcSpacing(1)}px *
            ${(props) => Math.min(props.$columnCount || 3, 3) - 1}
        ) /
        ${(props) => Math.min(props.$columnCount || 3, 3)}
    );
  min-width: 150px;
  ${flexContainerWithGap(1)}

  input {
    margin-top: 0;
    margin-bottom: 0;
  }

  ${(props) =>
    props.$inlinePlacement === 'formManagement' &&
    `
    min-width: 0;
    flex: 1 1 0;
  `}
`;

export const DataGridColumnHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${calcSpacing(0.5)}px;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleHeading
      : textStyleCss(theme.atlas.typography.semantic.body)};
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
  overflow: hidden;
  width: 100%;
`;

export const DataGridColumnHeaderText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DataGridRequiredTag = styled.span`
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmallSemibold
      : textStyleCss(theme.atlas.typography.semantic.caption)};
  font-weight: 600;
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
`;

export const DataGridInput = styled.div`
  ${inputBoxStyles}
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N600
      : props.theme.atlas.semantic.color.type.muted};
`;

export const DataGridAddRowButton = styled.button`
  ${iconButtonStyles}
  width: fit-content;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const DateTimeFieldsRow = styled.div`
  ${flexRowWithGap(1)}
`;

export const DateTimePickerWrapper = styled.div<{ $isDateWithTime?: boolean }>`
  position: relative;
  ${(props) =>
    props.$isDateWithTime
      ? 'flex: 0 0 60%; max-width: 60%;'
      : 'flex: 1 1 0; min-width: 0;'}
`;

export const DateTimeDisplayBox = styled.div`
  ${inputBoxStyles}
  display: flex;
  align-items: center;
  gap: ${calcSpacing(1)}px;
  height: 40px;
  padding: 8px 8px 8px 16px;
  cursor: pointer;
`;

export const DateTimeDisplayText = styled.span<{ $hasValue?: boolean }>`
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBody
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  color: ${(props) =>
    props.$hasValue
      ? props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N800
        : props.theme.atlas.semantic.color.type.default
      : props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N600
        : props.theme.atlas.semantic.color.type.muted};
  flex: 1;
  white-space: nowrap;
`;

export const HiddenNativeInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  overflow: hidden;
  pointer-events: none;
`;

export const DatabaseFieldInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const DatabaseFieldInfoTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DatabaseFieldInfoTitleText = styled.p`
  margin: 0;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySemibold
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  font-weight: 600;
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
`;

export const DatabaseFieldInfoDescription = styled.p`
  margin: 0;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySmall
      : textStyleCss(theme.atlas.typography.semantic.caption)};
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N600
      : props.theme.atlas.semantic.color.type.muted};
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${calcSpacing(8)}px ${calcSpacing(3)}px;
  gap: ${calcSpacing(3)}px;
  width: 100%;
  flex: 1;
`;

export const EmptyStateImage = styled.div`
  width: 380px;
  height: 380px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const EmptyStateText = styled.p`
  margin: 0;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleDisplaySmall
      : textStyleCss(theme.atlas.typography.semantic.headingH3)};
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
  text-align: center;
`;
